import { Row, Col, Card, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import SubtleBadge from '@/components/common/SubtleBadge';
import type { UserMetrics } from '../types';

const METRICS_CONFIG = [
  { key: 'visibilityScore' as const, label: 'دیده‌شدن در AI', suffix: '%', color: 'text-primary' },
  { key: 'avgPosition' as const, label: 'میانگین رتبه', suffix: '', color: 'text-info' },
  { key: 'brandsFound' as const, label: 'برندهای یافت‌شده', suffix: '', color: 'text-success' },
  { key: 'totalMentions' as const, label: 'مجموع منشن‌ها', suffix: '', color: 'text-warning' },
  { key: 'coMentions' as const, label: 'منشن مشترک', suffix: '', color: 'text-secondary' },
];

export function MetricsCards({ metrics }: { metrics: UserMetrics }) {
  return (
    <Row xs={2} md={3} xl={5} className="g-3">
      {METRICS_CONFIG.map(cfg => {
        const raw = metrics[cfg.key];
        const value = raw ?? '—';
        return (
          <Col key={cfg.key}>
            <Card className="h-100 shadow-sm">
              <Card.Body className="p-3">
                <p className="fs--1 fw-medium text-500 mb-1">{cfg.label}</p>
                <p className={`fs-3 fw-bold mb-0 ${cfg.color}`}>
                  {value}
                  {cfg.suffix && typeof raw === 'number' && (
                    <span className="fs-1 fw-semibold">{cfg.suffix}</span>
                  )}
                </p>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
    </Row>
  );
}

type StatusMeta = {
  bg: string;
  label: string;
  icon: IconProp;
  /** Solid badge with white text (e.g. running). */
  solid?: boolean;
  spin?: boolean;
};

function resolveStatusMeta(status: string): StatusMeta {
  const normalized =
    status === 'completed' || status === 'success'
      ? 'executed'
      : status === 'running' || status === 'processing' || status === 'in_progress'
        ? 'running'
        : status === 'queued'
          ? 'queued'
          : status === 'cancelled' || status === 'canceled'
            ? 'cancelled'
            : status === 'error'
              ? 'failed'
              : status;

  switch (normalized) {
    case 'executed':
      return { bg: 'success', label: 'اجرا شده', icon: 'check' };
    case 'partial':
      return { bg: 'info', label: 'ناقص', icon: 'check-double' };
    case 'failed':
      return { bg: 'danger', label: 'ناموفق', icon: 'times' };
    case 'running':
      return { bg: 'primary', label: 'در حال اجرا', icon: 'spinner', solid: true, spin: true };
    case 'queued':
      return { bg: 'warning', label: 'در صف', icon: 'list' };
    case 'cancelled':
      return { bg: 'secondary', label: 'لغو شده', icon: 'ban' };
    case 'draft':
      return { bg: 'secondary', label: 'پیش‌نویس', icon: 'file-alt' };
    default:
      return { bg: 'warning', label: 'در انتظار', icon: 'clock' };
  }
}

export function StatusBadge({
  status,
  fullWidth = true,
}: {
  status: string;
  /** Stretch across the table cell (default). Set false for inline headers. */
  fullWidth?: boolean;
}) {
  const { bg, label, icon, solid, spin } = resolveStatusMeta(status);

  const content = (
    <>
      {spin ? (
        <Spinner
          animation="border"
          size="sm"
          className="mx-1"
          style={{ width: '0.7rem', height: '0.7rem', borderWidth: '0.12em' }}
        />
      ) : (
        <FontAwesomeIcon icon={icon} transform="shrink-2" className="mx-1" />
      )}
      {label}
    </>
  );

  const badge = solid ? (
    <span
      className={`badge rounded-pill bg-${bg} text-white fs--2 fw-normal ${
        fullWidth ? 'w-100 d-flex justify-content-center align-items-center' : ''
      }`}
    >
      {content}
    </span>
  ) : (
    <SubtleBadge
      bg={bg}
      pill
      className={`fs--2 fw-normal ${
        fullWidth ? 'w-100 d-flex justify-content-center align-items-center' : ''
      }`}
    >
      {content}
    </SubtleBadge>
  );

  if (!fullWidth) return badge;

  return (
    <div
      className="w-100 d-flex align-items-center justify-content-center"
      style={{ height: '100%' }}
    >
      {badge}
    </div>
  );
}
